import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, OnChanges, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { MessageService } from 'primeng/api';
import { FilterService } from '../../services/filter.service';
import { ORPHANS_FILTER_OPTIONS } from '../../configs/filter-configs';
import { ManagedataService } from '../../../providers/input/managedata/managedata.service';

// Predefined order for RAG status values
const RAG_STATUS_ORDER = ['red', 'amber', 'green', 'grey'];

// Predefined order of magnitude for fields that appear in filter popups
const OPERATIONAL_CRITICALITY_ORDER = ['very high', 'high', 'medium', 'low'];
const BUSINESS_CRITICALITY_ORDER = ['select', 'tier 1 - critical', 'tier 2 - significant', 'tier 3 - important', 'tier 4 - minor', 'tier 5 - inconsequential'];
const IN_SUPPORT_ORDER = ['yes', 'extended', 'partially', 'no'];
const CONNECTED_ORDER = ['fully', 'partially', 'not connected'];

const fieldToValueMap = {
  rag: RAG_STATUS_ORDER,
  inSupport: IN_SUPPORT_ORDER,
  connected: CONNECTED_ORDER
}

export interface FilterField {
  key: string;
  label: string;
  type: 'dropdown' | 'text' | 'date' | 'multi-select';
  options?: any[];
  isCustomField?: boolean;
  isOrphans?: boolean;
  disabled?: boolean;
}

export interface FilterConfig {
  title: string;
  fields: FilterField[];
  data: any[];
  customFields?: any[];
  fieldOptions?: { [key: string]: any[] };
}

export interface FilterState {
  [key: string]: any;
}

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [MessageService],
  standalone: false
})
export class FilterComponent implements OnInit, OnChanges {
  @Input() config!: FilterConfig;
  @Input() currentFilters: FilterState = {};
  @Input() customFields: any[] = [];
  @Input() filterFields: FilterField[] = [];
  @Input() accountId: string = '';
  @Input() customFieldOptions: any = null;
  @Input() isVisible: boolean = false;
  @Output() onFiltersApplied = new EventEmitter<FilterState>();
  @Output() onFiltersCleared = new EventEmitter<void>();

  fieldOptions: { [key: string]: any[] } = {};
  selectedFilters: FilterState = {};
  multiSelectFilters: { [key: string]: string[] } = {};

  allFields: any[] = [];
  allCustomFields: any[] = [];
  isDataReady: boolean = false;
  isFilterPopupVisible: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private filterService: FilterService,
    private managedataService: ManagedataService
  ) { }

  ngOnInit(): void {
    this.allFields = [];
    this.allCustomFields = [];
    this.isDataReady = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customFieldOptions'] && changes['customFieldOptions'].currentValue) {
      this.isDataReady = false;
      setTimeout(() => {
        this.processAllDataInstantly();
      }, 0);
    }
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      this.isDataReady = false;
      setTimeout(() => {
        this.processAllDataInstantly();
      }, 0);
    }
  }

  private processAllDataInstantly(): void {
    if (!this.config) {
      return;
    }

    // Clear existing arrays to prevent duplicates
    this.allFields = [];
    this.allCustomFields = [];
    this.fieldOptions = {};

    const allFields: any[] = [];
    const allCustomFields: any[] = [];
    const fieldOptions: { [key: string]: any[] } = {};

    const fieldValues: { [key: string]: Set<string> } = {};

    if (this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'dropdown' || field.type === 'multi-select') {
          fieldValues[field.key] = new Set<string>();
        }
      });
    }

    // Always add custom fields to the UI first, then load options
    if (this.config.customFields?.length) {
      this.config.customFields.forEach(customField => {
        const key = `custom_${customField._id}`;

        // Add custom field to UI immediately with empty options
        allCustomFields.push({
          key: key,
          label: customField.label || customField.name || 'Custom Field',
          type: 'multi-select',
          options: []
        });
      });
    }

    if (this.config.data && this.config.data.length > 0) {
      this.config.data.forEach(item => {
        if (this.config.fields) {
          this.config.fields.forEach(field => {
            if (field.type === 'dropdown' || field.type === 'multi-select') {
              const value = this.getNestedValue(item, field.key);
              if (value && value.toString().trim() !== '') {
                fieldValues[field.key].add(value.toString().trim());
              }
            }
          });
        }

        // Custom field data processing is now handled by API call
      });
    }

    // Process custom fields using passed data or API
    if (this.config.customFields?.length) {
      if (this.customFieldOptions) {
        // Use passed custom field options
        this.processCustomFieldOptions(allCustomFields, fieldOptions);
      } else if (this.accountId) {
        // Fallback to API call if no custom field options passed
        this.loadCustomFieldOptions();
        return; // Return early as the rest will be handled in the callback
      }
    }

    if (this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'dropdown' || field.type === 'multi-select') {
          let options: any[] = [];

          if (field.isOrphans) {
            options = ORPHANS_FILTER_OPTIONS;
          } else if (field.key === 'benefits') {
            // Generate benefits options from data
            options = this.generateBenefitsOptions();
          } else if (field.key === 'criticality' || field.key in fieldToValueMap) {
            // Handle fields with predefined order arrays
            const values = fieldValues[field.key] || new Set<string>();
            const valuesLowerCase = new Set(Array.from(values).map(v => v.toLowerCase()));
            
            // Determine order array: criticality uses label to distinguish, others use fieldToValueMap
            let orderArray: string[];
            if (field.key === 'criticality') {
              // Use Business Criticality order for Application Data, Operational Criticality for OT Data
              orderArray = field.label === 'Business Criticality' 
                ? BUSINESS_CRITICALITY_ORDER 
                : OPERATIONAL_CRITICALITY_ORDER;
            } else {
              orderArray = fieldToValueMap[field.key as keyof typeof fieldToValueMap];
            }
            
            options = orderArray
              .filter(val => valuesLowerCase.has(val))
              .map(value => ({
                label: this.toSentenceCase(value),
                value: value
              }));
          } else {
            // Generate options from data values (alphabetical sort for other fields)
            const values = fieldValues[field.key] || new Set<string>();
            options = Array.from(values).sort().map(value => ({
              label: field.key === 'prgType' && value === 'it' ? 'IT' : this.toSentenceCase(value),
              value: value
            }));
          }

          fieldOptions[field.key] = options;

          allFields.push({
            key: field.key,
            label: field.label,
            type: field.type,
            options: options,
            disabled: field.disabled
          });
        } else if (field.type === 'text') {
          const placeholder = field.key === 'search'
            ? 'Search across names and descriptions...'
            : `Search in ${field.label.toLowerCase()}...`;

          allFields.push({
            key: field.key,
            label: field.label,
            type: field.type,
            placeholder: placeholder,
            disabled: field.disabled
          });
        }
      });
    }

    // Store the processed data
    this.fieldOptions = fieldOptions;
    this.allFields = allFields;
    this.allCustomFields = allCustomFields;

    // Finish processing if we're not using API call for custom fields
    if (!this.config.customFields?.length || this.customFieldOptions || !this.accountId) {
      this.finishProcessingData();
    }
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  onFieldChange(fieldKey: string, value: any): void {
    this.selectedFilters[fieldKey] = value;
  }

  onMultiSelectChange(fieldKey: string, value: string, event: any): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (!this.multiSelectFilters[fieldKey]) {
      this.multiSelectFilters[fieldKey] = [];
    }

    if (checked) {
      if (!this.multiSelectFilters[fieldKey].includes(value)) {
        this.multiSelectFilters[fieldKey].push(value);
      }
    } else {
      this.multiSelectFilters[fieldKey] = this.multiSelectFilters[fieldKey].filter(v => v !== value);
    }
  }

  /**
   * Toggle option selection when clicking on the entire option
   */
  toggleOptionSelection(fieldKey: string, value: string, event?: Event): void {
    if (event) {
      event.stopPropagation();

      // Check if the click is coming from the checkbox itself
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
        // Let the checkbox handle it naturally, don't prevent default
        return;
      }

      // If it's from the label or div, prevent the checkbox from changing naturally
      event.preventDefault();
    }

    if (!this.multiSelectFilters[fieldKey]) {
      this.multiSelectFilters[fieldKey] = [];
    }

    if (this.isOptionSelected(fieldKey, value)) {
      // If selected, remove it
      this.multiSelectFilters[fieldKey] = this.multiSelectFilters[fieldKey].filter(v => v !== value);
    } else {
      // If not selected, add it
      if (!this.multiSelectFilters[fieldKey].includes(value)) {
        this.multiSelectFilters[fieldKey].push(value);
      }
    }
  }

  onSelectAll(fieldKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      // Don't prevent default - let the checkbox change naturally
    }

    const field = this.allFields.find(f => f.key === fieldKey) || this.allCustomFields.find(f => f.key === fieldKey);
    if (field && field.options) {
      this.multiSelectFilters[fieldKey] = field.options.map((option: any) => option.value);
    }
  }

  onUnselectAll(fieldKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      // Don't prevent default - let the checkbox change naturally
    }

    this.multiSelectFilters[fieldKey] = [];
  }

  /**
   * Handle checkbox change for Select All/Unselect All
   */
  onSelectAllCheckboxChange(fieldKey: string, event: Event): void {
    // Don't do anything if there are no options
    if (!this.hasOptions(fieldKey)) {
      return;
    }

    event.stopPropagation();
    // Don't prevent default - let the checkbox change naturally

    const isChecked = (event.target as HTMLInputElement).checked;

    if (isChecked) {
      this.onSelectAll(fieldKey, event);
    } else {
      this.onUnselectAll(fieldKey, event);
    }
  }

  /**
   * Handle label click for Select All/Unselect All (without interfering with checkbox)
   */
  onSelectAllLabelClick(fieldKey: string, event: Event): void {
    // Don't do anything if there are no options
    if (!this.hasOptions(fieldKey)) {
      return;
    }

    // Check if the click was on the checkbox itself
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {
      // Let the checkbox handle it naturally
      return;
    }

    // Otherwise, handle the label click
    event.stopPropagation();
    event.preventDefault();

    if (this.isAllSelected(fieldKey)) {
      this.onUnselectAll(fieldKey, event);
    } else {
      this.onSelectAll(fieldKey, event);
    }
  }

  isAllSelected(fieldKey: string): boolean {
    const field = this.allFields.find(f => f.key === fieldKey) || this.allCustomFields.find(f => f.key === fieldKey);
    if (!field || !field.options || field.options.length === 0) return false;
    return this.multiSelectFilters[fieldKey]?.length === field.options.length;
  }

  isSomeSelected(fieldKey: string): boolean {
    return this.multiSelectFilters[fieldKey]?.length > 0 && !this.isAllSelected(fieldKey);
  }

  isOptionSelected(fieldKey: string, value: string): boolean {
    return this.multiSelectFilters[fieldKey]?.includes(value) || false;
  }

  hasOptions(fieldKey: string): boolean {
    const options = this.getFieldOptions(fieldKey);
    const hasOptions = options && options.length > 0;
    return hasOptions;
  }

  private openMultiSelects: { [key: string]: boolean } = {};

  toggleMultiSelect(fieldKey: string): void {
    // Close all other dropdowns first
    Object.keys(this.openMultiSelects).forEach(key => {
      if (key !== fieldKey) {
        this.openMultiSelects[key] = false;
      }
    });

    // Toggle the current dropdown
    this.openMultiSelects[fieldKey] = !this.openMultiSelects[fieldKey];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close all open multi-selects when clicking outside
    const target = event.target as HTMLElement;
    const isInsideMultiSelect = target.closest('.multi-select-container');

    if (!isInsideMultiSelect) {
      // Close all open dropdowns
      this.closeAllDropdowns();
    }
  }

  private closeAllDropdowns(): void {
    Object.keys(this.openMultiSelects).forEach(key => {
      this.openMultiSelects[key] = false;
    });
  }

  onPopupClick(event: Event): void {
    // Stop propagation to prevent closing the popup
    event.stopPropagation();

    // Check if click is on a multi-select container
    const target = event.target as HTMLElement;
    const isInsideMultiSelect = target.closest('.multi-select-container');

    // If not clicking on a multi-select, close all dropdowns
    if (!isInsideMultiSelect) {
      this.closeAllDropdowns();
    }
  }

  isMultiSelectOpen(fieldKey: string): boolean {
    return this.openMultiSelects[fieldKey] || false;
  }

  getMultiSelectDisplayText(fieldKey: string): string {
    const selectedValues = this.multiSelectFilters[fieldKey] || [];
    if (selectedValues.length === 0) {
      return 'Select All';
    } else if (selectedValues.length === 1) {
      const field = this.allFields.find(f => f.key === fieldKey) || this.allCustomFields.find(f => f.key === fieldKey);
      const option = field?.options?.find((opt: any) => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    } else {
      return `${selectedValues.length} selected`;
    }
  }

  clearField(fieldKey: string): void {
    const field = this.allFields.find(f => f.key === fieldKey);
    const customField = this.allCustomFields.find(f => f.key === fieldKey);

    if (field && field.type === 'multi-select') {
      this.multiSelectFilters[fieldKey] = [];
    } else if (customField && customField.type === 'multi-select') {
      this.multiSelectFilters[fieldKey] = [];
    } else {
      this.selectedFilters[fieldKey] = '';
    }
  }

  openFilterPopup(): void {
    this.isFilterPopupVisible = true;
    this.processAllDataInstantly();
  }

  onClosePopup(): void {
    this.isFilterPopupVisible = false;
  }

  onApplyFilters(): void {
    const filters = { ...this.selectedFilters };

    // Add multi-select values to filters
    Object.keys(this.multiSelectFilters).forEach(key => {
      if (this.multiSelectFilters[key].length > 0) {
        filters[key] = this.multiSelectFilters[key];
      }
    });

    this.currentFilters = filters;
    this.isFilterPopupVisible = false;
    this.onFiltersApplied.emit(filters);
  }

  onResetFilters(): void {
    this.selectedFilters = {};
    this.multiSelectFilters = {};

    if (this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'multi-select') {
          this.multiSelectFilters[field.key] = [];
        } else {
          this.selectedFilters[field.key] = '';
        }
      });
    }

    if (this.config.customFields) {
      this.config.customFields.forEach(customField => {
        const key = `custom_${customField._id}`;
        this.multiSelectFilters[key] = [];
      });
    }
  }

  onCancelFilters(): void {
    this.isFilterPopupVisible = false;
  }

  clearAllFilters(): void {
    this.currentFilters = {};
    this.multiSelectFilters = {};
    this.onFiltersCleared.emit();
  }

  hasActiveFilters(): boolean {
    // Check both currentFilters and multiSelectFilters
    const hasCurrentFilters = this.filterService.hasActiveFilters(this.currentFilters);
    const hasMultiSelectFilters = Object.values(this.multiSelectFilters).some(values =>
      Array.isArray(values) && values.length > 0
    );
    return hasCurrentFilters || hasMultiSelectFilters;
  }

  getFieldOptions(fieldKey: string): any[] {
    const options = this.fieldOptions[fieldKey] || [];
    return options;
  }

  hasValue(fieldKey: string): boolean {
    const field = this.allFields.find(f => f.key === fieldKey);
    const customField = this.allCustomFields.find(f => f.key === fieldKey);

    if (field && field.type === 'multi-select') {
      return this.multiSelectFilters[fieldKey]?.length > 0;
    } else if (customField && customField.type === 'multi-select') {
      return this.multiSelectFilters[fieldKey]?.length > 0;
    }
    const value = this.selectedFilters[fieldKey];
    return value && value !== '' && value !== 'Please Select';
  }

  trackByFieldKey(index: number, field: any): string {
    return field.key;
  }

  trackByOptionValue(index: number, option: any): string {
    return option.value;
  }

  /**
   * Generate benefits options from data
   */
  private generateBenefitsOptions(): any[] {
    const benefitsSet = new Set<string>();

    if (this.config.data) {
      this.config.data.forEach((item, index) => {

        // Check if there's a benefits array in the item (most likely structure)
        if (item.benefits && Array.isArray(item.benefits)) {
          item.benefits.forEach((benefit: any) => {
            if (benefit.benefit && benefit.benefit.toString().trim() !== '') {
              benefitsSet.add(benefit.benefit.toString().trim());
            }
          });
        }

        // Also check benefits_realisations array if it exists
        if (item.benefits_realisations && Array.isArray(item.benefits_realisations)) {
          item.benefits_realisations.forEach((benefit: any) => {
            if (benefit.benefit && benefit.benefit.toString().trim() !== '') {
              benefitsSet.add(benefit.benefit.toString().trim());
            }
          });
        }

      });
    }

    const result = Array.from(benefitsSet).sort().map(benefit => ({
      label: this.toSentenceCase(benefit),
      value: benefit
    }));

    return result;
  }


  private toSentenceCase(text: string): string {
    if (!text) return text;

    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  toggleOption(fieldKey: string, value: any, event: any): void {
    const checked = event.target.checked;
    if (!Array.isArray(this.selectedFilters[fieldKey])) {
      this.selectedFilters[fieldKey] = [];
    }

    if (checked) {
      if (!this.selectedFilters[fieldKey].includes(value)) {
        this.selectedFilters[fieldKey].push(value);
      }
    } else {
      this.selectedFilters[fieldKey] = this.selectedFilters[fieldKey].filter((v: any) => v !== value);
    }
  }

  isSelected(fieldKey: string, value: any): boolean {
    return Array.isArray(this.selectedFilters[fieldKey]) && this.selectedFilters[fieldKey].includes(value);
  }

  toggleSelectAll(fieldKey: string): void {
    const options = this.getFieldOptions(fieldKey).map(o => o.value);
    if (this.isAllSelected(fieldKey)) {
      this.selectedFilters[fieldKey] = [];
    } else {
      this.selectedFilters[fieldKey] = [...options];
    }
  }

  private loadCustomFieldOptions(): void {
    const type: "Project" | "Supplier" | "Applications" | "OTData" = "Project";   // TODO - fetch dataType with few lines to avoid complexity

    if (!this.accountId || !this.config.customFields) {
      this.finishProcessingData();
      return;
    }

    this.managedataService[`getCustomFieldsFor${type}`](this.accountId).subscribe({
      next: (response) => {
        this.processCustomFieldResponse(response);
        this.finishProcessingData();
      },
      error: (error) => {
        console.error('Error loading custom field options:', error);
        this.finishProcessingData();
      }
    });
  }

  private processCustomFieldOptions(allCustomFields: any[], fieldOptions: { [key: string]: any[] }): void {

    if (!this.customFieldOptions || !this.config.customFields) {
      return;
    }

    this.config.customFields.forEach(customField => {
      const key = `custom_${customField._id}`;

      // Get the values from the passed customFieldOptions
      const fieldValues = this.customFieldOptions[customField._id];

      let options: any[] = [];
      if (fieldValues && Array.isArray(fieldValues) && fieldValues.length > 0) {
        options = fieldValues
          .filter((value: any) => value && value.toString().trim() !== '')
          .map((value: any) => ({
            label: this.toSentenceCase(value.toString()),
            value: value.toString()
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
      }

      fieldOptions[key] = options;

      // Update the existing custom field with the new options
      const existingField = allCustomFields.find(field => field.key === key);
      if (existingField) {
        existingField.options = options;
      } else {
        // If field doesn't exist, add it (shouldn't happen, but safety check)
        allCustomFields.push({
          key: key,
          label: customField.label || customField.name || 'Custom Field',
          type: 'multi-select',
          options: options
        });
      }
    });

  }

  private processCustomFieldResponse(response: any): void {

    if (!response || !this.config.customFields) {
      return;
    }

    const fieldOptions: { [key: string]: any[] } = {};

    this.config.customFields.forEach(customField => {
      const key = `custom_${customField._id}`;

      // Find the corresponding data in the API response
      const apiFieldData = response.find((item: any) => item._id === customField._id);

      let options: any[] = [];
      if (apiFieldData && apiFieldData.values && Array.isArray(apiFieldData.values)) {
        options = apiFieldData.values
          .filter((value: any) => value && value.toString().trim() !== '')
          .map((value: any) => ({
            label: this.toSentenceCase(value.toString()),
            value: value.toString()
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
      } else {
      }

      fieldOptions[key] = options;

      // Update the existing custom field with the new options
      const existingField = this.allCustomFields.find(field => field.key === key);
      if (existingField) {
        existingField.options = options;
      } else {
      }
    });

    // Merge with existing fieldOptions
    Object.assign(this.fieldOptions, fieldOptions);
  }

  private finishProcessingData(): void {

    const selectedFilters: FilterState = {};
    const multiSelectFilters: { [key: string]: string[] } = {};

    if (this.currentFilters) {
      Object.assign(selectedFilters, this.currentFilters);
    }

    // Preserve existing multi-select filter values
    if (this.multiSelectFilters) {
      Object.assign(multiSelectFilters, this.multiSelectFilters);
    }

    if (this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'multi-select') {
          if (!multiSelectFilters[field.key]) {
            multiSelectFilters[field.key] = [];
          }
        } else {
          if (!selectedFilters[field.key]) {
            selectedFilters[field.key] = '';
          }
        }
      });
    }

    // Initialize multi-select filters for custom fields
    if (this.config.customFields?.length) {
      this.config.customFields.forEach(customField => {
        const key = `custom_${customField._id}`;
        if (!multiSelectFilters[key]) {
          multiSelectFilters[key] = [];
        }
      });
    }

    this.selectedFilters = selectedFilters;
    this.multiSelectFilters = multiSelectFilters;
    this.isDataReady = true;
    this.cdr.detectChanges();
  }

}